import { Injectable } from '@angular/core';
import {
    FiBackendService,
    IBackendErrorContentDetails,
    IBackendErrorData,
} from '@fi-shared/fi-backend/fi-backend.service';
import { AppTextService } from '@fi-shared/app-text';
import { StatusCodeType } from '@fi-shared/types/status-code-type';
import { FiMobileAppHelperService } from './mobile-app-helper.service';
import { ActionFailureReasonCode } from '@fi-shared/types/action-failed-reason-code';
import { FiErrorMessageSharedService } from '@fi-shared/fi-shared-services/fi-error-message-shared.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { IFiDialogOptions, IFiDialogResult } from '@fi-shared/fi-dialogs/fi-dialogs';
import { AccessRightType, ApplicationSettingsType } from '@fi-shared/types';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';

@Injectable()
export class ErrorMessageService {
    constructor(
        private appTextService: AppTextService,
        private fiDialogsService: FiDialogsService,
        private mobileAppHelperService: FiMobileAppHelperService,
        private fiBackend: FiBackendService,
        private stateService: FiStateService,
        private userDataService: FiUserDataService,
        private errorMessageSharedService: FiErrorMessageSharedService,
        private fiSignalRService: FiSignalRService,
        private fiStateService: FiStateService,
        private accessToApplicationTypeService: AccessToApplicationTypeService,
        private fiOnboardingHelperService: FiOnboardingHelperService,
    ) {}

    private readonly notifyErrorCodes = [
        ActionFailureReasonCode.User_AccessRequestAlreadyAccepted,
        ActionFailureReasonCode.User_AccessRequestAlreadyRejected,
    ];

    show(response: IBackendErrorData) {
        if (response) {
            const reason = response?.error?.Reason;
            if (
                response.status === StatusCodeType.Unauthorized ||
                reason === ActionFailureReasonCode.User_TokenExpired ||
                reason === ActionFailureReasonCode.User_TokenDeactivated ||
                reason === ActionFailureReasonCode.General_UpgradeInProcess
            )
                return;

            // FI-4032 Error dialog when pressing links in ios app
            if (response.status === StatusCodeType.NetworkError && this.mobileAppHelperService.mobileAppMode) return;

            // todo find the way to separate net::ERR_NAME_NOT_RESOLVED/net::ERR_INSECURE_RESPONSE/(cancelled)
            if (response.status === StatusCodeType.Aborted)
                return this.fiDialogsService.error({
                    messageText: 'net::ERR...',
                });

            let description = response?.error?.Description;
            if (!description && typeof response?.error === typeof '') {
                // error can be string in case of internal server error
                description = {
                    MessageText: response?.error as string,
                };
            }
            const errorMessageData = this.errorMessageSharedService.getErrorMessageData(description);
            const errorData = response?.error?.Data;
            const dialog = this.getDialogRef(reason, errorMessageData, errorData);

            if (
                reason == ActionFailureReasonCode.General_AccessDenied_Object &&
                this.mobileAppHelperService.mobileAppMode
            ) {
                dialog.afterClosed(() => {
                    window.history.back();
                });
            }

            return dialog;
        }
    }

    subscribeOnBackendErrors() {
        this.fiBackend.onHttpError$.subscribe((errorData) => {
            const dialogRef = this.show(errorData);
            // https://chainformation.atlassian.net/browse/FI-3961
            // dialogRef can be null
            if (dialogRef) {
                dialogRef.afterClosed(() => {
                    if (this.userDataService.authorized && this.stateService.current.name.startsWith('Login.')) {
                        this.stateService.go(FiStateNames.Home._name);
                    }
                });
            }
        });

        this.fiSignalRService.backgroundExceptionEvent.subscribe((data) => {
            const errorMessageData = this.errorMessageSharedService.getErrorMessageData(data.Description);
            this.fiDialogsService.error({
                messageText: errorMessageData.messageText,
                headerText: errorMessageData.headerText,
            });
        });
    }

    private getDialogRef(
        reason: ActionFailureReasonCode,
        errorMessageData: {
            messageText: string;
            headerText: string;
            isDefaultMessage: boolean;
        },
        errorData: IBackendErrorContentDetails,
    ): IFiDialogResult<any> {
        // special case for deleting elements which are used in onboardings
        if (reason === ActionFailureReasonCode.Onboarding_DeleteElementInUse) {
            const dialogRef = this.fiOnboardingHelperService.showElementInUseDialog(
                errorData.OnboardingIds,
                errorMessageData.messageText,
                errorMessageData.headerText,
            );
            return dialogRef;
        }

        if (this.notifyErrorCodes.includes(reason)) {
            return this.fiDialogsService.blueInfoDialog({
                messageText: errorMessageData.messageText,
                headerText: errorMessageData.headerText,
                isTopCloseButtonShown: false,
                isBigSizeTopBorder: true,
            });
        }

        return this.fiDialogsService.error({
            messageText: errorMessageData.messageText,
            headerText: errorMessageData.headerText,
        });
    }
}
